import { Platform } from 'react-native'
import { api, TrellisError } from './api'
import { supabase } from './supabase'

// Stripe publishable key - safe to expose
const STRIPE_PUBLISHABLE_KEY = process.env.EXPO_PUBLIC_STRIPE_PUBLISHABLE_KEY || 'pk_test_placeholder'

export function getStripePublishableKey(): string {
  return STRIPE_PUBLISHABLE_KEY
}

/**
 * Create a payment intent for a reservation
 */
export async function createReservationPaymentIntent(reservationId: string): Promise<{
  clientSecret: string; paymentIntentId: string; amount: number; error?: string
}> {
  try {
    const data = await api.createPaymentIntent.fetch({
      type: 'reservation',
      reservation_id: reservationId,
    })
    return data as any
  } catch (error: any) {
    console.error('Error creating payment intent:', error)
    return { clientSecret: '', paymentIntentId: '', amount: 0, error: error.message }
  }
}

/**
 * Create a payment intent for a POS sale
 */
export async function createSalePaymentIntent(saleId: string, amount?: number): Promise<{
  clientSecret: string; paymentIntentId: string; amount: number; error?: string
}> {
  try {
    const data = await api.createPaymentIntent.fetch({
      type: 'sale',
      sale_id: saleId,
      amount: amount ? Math.round(amount * 100) : undefined,
    })
    return data as any
  } catch (error: any) {
    console.error('Error creating sale payment intent:', error)
    return { clientSecret: '', paymentIntentId: '', amount: 0, error: error.message }
  }
}

/**
 * Start Stripe Connect onboarding for a market
 */
export async function startConnectOnboarding(marketId: string): Promise<{
  url: string; accountId?: string; error?: string
}> {
  try {
    const baseUrl = Platform.OS === 'web'
      ? window.location.origin
      : 'https://kirpputori.dev.sakualy.ai'

    const data = await api.createConnectAccount.fetch({
      market_id: marketId,
      return_url: `${baseUrl}/market/${marketId}/settings?stripe=success`,
      refresh_url: `${baseUrl}/market/${marketId}/settings?stripe=refresh`,
    })
    return data as any
  } catch (error: any) {
    console.error('Error starting Connect onboarding:', error)
    return { url: '', error: error.message }
  }
}

/**
 * Get Stripe Express dashboard login link
 */
export async function getConnectDashboardLink(marketId: string): Promise<{
  url: string; error?: string; needsOnboarding?: boolean
}> {
  try {
    const data = await api.createConnectLoginLink.fetch({ market_id: marketId })
    return data as any
  } catch (error: any) {
    if (error instanceof TrellisError) {
      return { url: '', error: error.message, needsOnboarding: error.code === 'NEEDS_ONBOARDING' }
    }
    console.error('Error getting dashboard link:', error)
    return { url: '', error: error.message }
  }
}

/**
 * Process a payout for a seller
 */
export async function processPayout(payoutId: string): Promise<{
  success: boolean; status?: string; transferId?: string; error?: string
}> {
  try {
    const data = await api.processPayout.fetch({ payout_id: payoutId })
    return data as any
  } catch (error: any) {
    console.error('Error processing payout:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Calculate a new payout for a seller
 */
export async function calculatePayout(
  sellerId: string, marketId: string, periodStart?: string, periodEnd?: string
): Promise<{
  success: boolean; payout?: any; calculation?: any; error?: string
}> {
  try {
    const data = await api.processPayout.fetch({
      seller_id: sellerId,
      market_id: marketId,
      period_start: periodStart,
      period_end: periodEnd,
    })
    return data as any
  } catch (error: any) {
    console.error('Error calculating payout:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Format currency for display
 */
export function formatCurrency(amount: number, currency: string = 'EUR'): string {
  return new Intl.NumberFormat('fi-FI', { style: 'currency', currency }).format(amount)
}

/**
 * Start Stripe Connect onboarding for a seller (to receive payouts)
 */
export async function startSellerConnectOnboarding(): Promise<{
  url: string; accountId?: string; error?: string
}> {
  try {
    const baseUrl = Platform.OS === 'web'
      ? window.location.origin
      : 'https://kirpputori.dev.sakualy.ai'

    const data = await api.createSellerConnectAccount.fetch({
      return_url: `${baseUrl}/profile?stripe=success`,
      refresh_url: `${baseUrl}/profile?stripe=refresh`,
    })
    return data as any
  } catch (error: any) {
    console.error('Error starting seller onboarding:', error)
    return { url: '', error: error.message }
  }
}

/**
 * Calculate pending payouts for a market (dry run)
 * Falls back to local calculation if edge function is unavailable
 */
export async function calculateMarketPayouts(
  marketId: string, periodStart?: string, periodEnd?: string
): Promise<{
  success: boolean; summary?: any; payouts?: any[]; stripe_balance?: number
  insufficient_funds?: boolean; top_up_needed?: number; error?: string
}> {
  try {
    const data = await api.processSellerPayouts.fetch({
      market_id: marketId,
      period_start: periodStart,
      period_end: periodEnd,
      dry_run: true,
    })
    return data as any
  } catch (error: any) {
    // Edge function failed — fall back to local calculation
    console.log('Edge function failed, using local calculation:', error.message)
    return await calculatePayoutsLocally(marketId, periodStart, periodEnd)
  }
}

/**
 * Process payouts for sellers in a market
 */
export async function processMarketPayouts(
  marketId: string, sellerIds?: string[], periodStart?: string, periodEnd?: string
): Promise<{
  success: boolean; summary?: any; processed?: any[]; failed?: any[]; error?: string
}> {
  try {
    const data = await api.processSellerPayouts.fetch({
      market_id: marketId,
      seller_ids: sellerIds,
      period_start: periodStart,
      period_end: periodEnd,
      dry_run: false,
    })
    return data as any
  } catch (error: any) {
    console.error('Error processing payouts:', error)
    return { success: false, error: error.message }
  }
}

// ---------------------------------------------------------------------------
// Local fallback for payout calculation
// ---------------------------------------------------------------------------

async function calculatePayoutsLocally(
  marketId: string, periodStart?: string, periodEnd?: string
) {
  const { data: market } = await supabase
    .from('flea_markets')
    .select('id, name, commission_percentage')
    .eq('id', marketId)
    .single()

  if (!market) throw new Error('Market not found')

  const commissionRate = (market.commission_percentage || 15) / 100
  const endDate = periodEnd || new Date().toISOString().split('T')[0]
  const startDate = periodStart || new Date(Date.now() - 30 * 86400000).toISOString().split('T')[0]

  const { data: soldProducts } = await supabase
    .from('products')
    .select('id, price, seller_id, sold_at, reservation:space_reservations!products_reservation_id_fkey(id, market_id:flea_market_id)')
    .eq('status', 'sold')
    .gte('sold_at', startDate)
    .lte('sold_at', endDate)

  const marketProducts = (soldProducts || []).filter((p: any) => {
    const r = Array.isArray(p.reservation) ? p.reservation[0] : p.reservation
    return r?.market_id === marketId
  })

  const sellerIds = [...new Set(marketProducts.map((p: any) => p.seller_id))]
  if (!sellerIds.length) {
    return {
      success: true,
      summary: { total_gross: 0, total_commission: 0, total_net: 0, seller_count: 0, sellers_with_stripe: 0, sellers_without_stripe: 0, items_count: 0 },
      payouts: [],
    }
  }

  const { data: sellers } = await supabase
    .from('users')
    .select('id, email, display_name, first_name, last_name, stripe_account_id')
    .in('id', sellerIds)

  const sellerMap = new Map((sellers || []).map((s: any) => [s.id, s]))

  const { data: existingPayouts } = await supabase
    .from('payout_items')
    .select('product_id')
    .in('product_id', marketProducts.map((p: any) => p.id))

  const paidIds = new Set((existingPayouts || []).map((p: any) => p.product_id))
  const bySeller = new Map<string, { gross: number; items: number }>()

  for (const product of marketProducts) {
    if (paidIds.has(product.id)) continue
    const cur = bySeller.get(product.seller_id) || { gross: 0, items: 0 }
    cur.gross += Number(product.price) || 0
    cur.items += 1
    bySeller.set(product.seller_id, cur)
  }

  const payouts: any[] = []
  let totalGross = 0, totalCommission = 0, totalNet = 0, totalItems = 0
  let withStripe = 0, withoutStripe = 0

  for (const [sid, { gross, items }] of bySeller) {
    const s = sellerMap.get(sid)
    if (!s) continue
    const commission = gross * commissionRate
    const net = gross - commission
    const name = s.display_name || `${s.first_name || ''} ${s.last_name || ''}`.trim() || s.email?.split('@')[0] || 'Unknown'
    const hasStripe = !!s.stripe_account_id

    payouts.push({
      seller_id: sid, seller_name: name, seller_email: s.email || '',
      stripe_account_id: s.stripe_account_id || null,
      gross_amount: gross, commission_amount: commission, net_amount: net,
      items_sold: items, can_pay: hasStripe,
    })
    totalGross += gross; totalCommission += commission; totalNet += net; totalItems += items
    hasStripe ? withStripe++ : withoutStripe++
  }

  payouts.sort((a, b) => b.net_amount - a.net_amount)

  return {
    success: true,
    summary: {
      total_gross: totalGross, total_commission: totalCommission, total_net: totalNet,
      seller_count: payouts.length, sellers_with_stripe: withStripe,
      sellers_without_stripe: withoutStripe, items_count: totalItems,
    },
    payouts,
  }
}
